import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import BACKEND_URL from '../config/backendUrl';
import backendApi from '../services/backendApi';
import { getIntercomDeviceId, isIntercomTvDashboard } from '../utils/intercomDevice';
import { startIncomingRingtone, stopIncomingRingtone } from '../utils/voiceRingtone';
import { useAuth } from './AuthContext';

const VoiceCallContext = createContext(null);
const IDLE_CALL = { status: 'idle', call: null, error: null, muted: false };

const emitWithAck = (socket, eventName, payload) => new Promise((resolve, reject) => {
  socket.timeout(6000).emit(eventName, payload, (timeoutError, response) => {
    if (timeoutError) return reject(new Error('Le service d’appel ne répond pas'));
    if (!response?.ok) return reject(new Error(response?.error || 'Appel impossible'));
    resolve(response);
  });
});

export function VoiceCallProvider({ children }) {
  const { user, isInternalUser } = useAuth();
  const [voiceCall, setVoiceCall] = useState(IDLE_CALL);
  const socketRef = useRef(null);
  const peerRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const currentCallRef = useRef(null);
  const pendingCandidatesRef = useRef([]);

  const releaseMedia = useCallback(() => {
    peerRef.current?.close();
    peerRef.current = null;
    localStreamRef.current?.getTracks().forEach(track => track.stop());
    localStreamRef.current = null;
    pendingCandidatesRef.current = [];
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
  }, []);

  const resetCall = useCallback((status = 'idle', error = null) => {
    stopIncomingRingtone();
    releaseMedia();
    currentCallRef.current = null;
    setVoiceCall(status === 'idle' ? IDLE_CALL : { status, call: null, error, muted: false });
  }, [releaseMedia]);

  const acquireMicrophone = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) throw new Error('Les appels audio ne sont pas pris en charge par ce navigateur');
    if (!localStreamRef.current) {
      localStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: false,
      });
    }
    return localStreamRef.current;
  }, []);

  const createPeer = useCallback(async call => {
    if (peerRef.current) return peerRef.current;
    const stream = await acquireMicrophone();
    const peer = new RTCPeerConnection({ iceServers: call.ice_servers || [] });
    stream.getTracks().forEach(track => peer.addTrack(track, stream));
    peer.onicecandidate = event => {
      if (event.candidate) socketRef.current?.emit('webrtc:ice-candidate', { callId: call.id, candidate: event.candidate });
    };
    peer.ontrack = event => {
      if (!remoteAudioRef.current) return;
      remoteAudioRef.current.srcObject = event.streams[0];
      remoteAudioRef.current.play().catch(() => {});
    };
    peer.onconnectionstatechange = () => {
      if (peer.connectionState === 'connected') {
        setVoiceCall(current => ({ ...current, status: 'active' }));
      } else if (['failed', 'closed'].includes(peer.connectionState) && currentCallRef.current) {
        setVoiceCall(current => ({ ...current, status: 'error', error: 'La connexion audio a été interrompue' }));
      }
    };
    peerRef.current = peer;
    return peer;
  }, [acquireMicrophone]);

  useEffect(() => {
    if (!user || !isInternalUser) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      resetCall();
      return undefined;
    }

    const socket = io(BACKEND_URL, {
      auth: {
        token: backendApi.getAuthToken(),
        deviceId: getIntercomDeviceId(),
        isTv: isIntercomTvDashboard(),
      },
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('call:incoming', ({ call }) => {
      if (currentCallRef.current) {
        socket.emit('call:reject', { callId: call.id, reason: 'busy' });
        return;
      }
      currentCallRef.current = call;
      setVoiceCall({ status: 'incoming', call, error: null, muted: false });
      startIncomingRingtone();
    });

    // Le socket authentifié est partagé avec le chat pour faire apparaître les
    // messages immédiatement, sans ouvrir une seconde connexion temps réel.
    socket.on('chat:message', ({ message }) => {
      window.dispatchEvent(new CustomEvent('team-chat:message', { detail: message }));
    });
    socket.on('chat:read', detail => {
      window.dispatchEvent(new CustomEvent('team-chat:read', { detail }));
    });

    socket.on('call:accepted', async ({ call }) => {
      currentCallRef.current = call;
      setVoiceCall(current => ({ ...current, status: 'connecting', call, error: null }));
      try {
        const peer = await createPeer(call);
        const offer = await peer.createOffer();
        await peer.setLocalDescription(offer);
        socket.emit('webrtc:offer', { callId: call.id, description: peer.localDescription });
      } catch (error) {
        socket.emit('call:end', { callId: call.id, reason: 'microphone_error' });
        resetCall('error', error.message || 'Impossible d’accéder au microphone');
      }
    });

    socket.on('webrtc:offer', async ({ callId, description }) => {
      const call = currentCallRef.current;
      if (!call || call.id !== callId) return;
      try {
        const peer = await createPeer(call);
        await peer.setRemoteDescription(description);
        for (const candidate of pendingCandidatesRef.current.splice(0)) await peer.addIceCandidate(candidate);
        const answer = await peer.createAnswer();
        await peer.setLocalDescription(answer);
        socket.emit('webrtc:answer', { callId, description: peer.localDescription });
      } catch (error) {
        socket.emit('call:end', { callId, reason: 'connection_error' });
        resetCall('error', 'Impossible d’établir la connexion audio');
      }
    });

    socket.on('webrtc:answer', async ({ callId, description }) => {
      if (currentCallRef.current?.id !== callId || !peerRef.current) return;
      try {
        await peerRef.current.setRemoteDescription(description);
        for (const candidate of pendingCandidatesRef.current.splice(0)) await peerRef.current.addIceCandidate(candidate);
      } catch (error) {
        resetCall('error', 'La réponse audio reçue est invalide');
      }
    });

    socket.on('webrtc:ice-candidate', async ({ callId, candidate }) => {
      if (currentCallRef.current?.id !== callId || !candidate) return;
      if (!peerRef.current?.remoteDescription) pendingCandidatesRef.current.push(candidate);
      else {
        try { await peerRef.current.addIceCandidate(candidate); } catch (error) { /* Un candidat isolé peut être ignoré. */ }
      }
    });

    socket.on('call:answered-elsewhere', ({ callId }) => {
      if (currentCallRef.current?.id === callId) resetCall();
    });
    socket.on('call:ended', ({ call, reason }) => {
      if (currentCallRef.current?.id !== call.id) return;
      const labels = { rejected: 'Appel refusé', timeout: 'Aucune réponse', busy: 'Collaborateur occupé', disconnected: 'Connexion interrompue' };
      resetCall('ended', labels[reason] || null);
      window.setTimeout(() => setVoiceCall(current => current.status === 'ended' ? IDLE_CALL : current), 2200);
    });
    socket.on('connect_error', error => {
      if (currentCallRef.current) resetCall('error', error.message || 'Service d’appel indisponible');
    });

    return () => {
      socket.disconnect();
      if (socketRef.current === socket) socketRef.current = null;
      resetCall();
    };
  }, [createPeer, isInternalUser, resetCall, user]);

  const startCall = useCallback(async member => {
    const socket = socketRef.current;
    if (!socket?.connected) throw new Error('Le service d’appel est en cours de connexion');
    if (currentCallRef.current) throw new Error('Un appel est déjà en cours');
    const response = await emitWithAck(socket, 'call:start', { targetId: member.id, targetName: member.name });
    currentCallRef.current = response.call;
    setVoiceCall({ status: 'ringing', call: response.call, error: null, muted: false });
    return response.call;
  }, []);

  const acceptCall = useCallback(async () => {
    const call = currentCallRef.current;
    if (!call) return;
    stopIncomingRingtone();
    try {
      await acquireMicrophone();
      await emitWithAck(socketRef.current, 'call:accept', { callId: call.id });
      setVoiceCall(current => ({ ...current, status: 'connecting', error: null }));
    } catch (error) {
      socketRef.current?.emit('call:reject', { callId: call.id, reason: 'microphone_error' });
      resetCall('error', error.name === 'NotAllowedError' ? 'Autorise le microphone pour répondre' : error.message);
    }
  }, [acquireMicrophone, resetCall]);

  const rejectCall = useCallback(() => {
    const call = currentCallRef.current;
    if (call) socketRef.current?.emit('call:reject', { callId: call.id, reason: 'rejected' });
    resetCall();
  }, [resetCall]);

  const endCall = useCallback(() => {
    const call = currentCallRef.current;
    if (call) socketRef.current?.emit('call:end', { callId: call.id, reason: 'ended' });
    resetCall();
  }, [resetCall]);

  const toggleMute = useCallback(() => {
    const track = localStreamRef.current?.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setVoiceCall(current => ({ ...current, muted: !track.enabled }));
  }, []);

  const value = useMemo(() => ({ ...voiceCall, startCall, acceptCall, rejectCall, endCall, toggleMute }),
    [acceptCall, endCall, rejectCall, startCall, toggleMute, voiceCall]);

  return (
    <VoiceCallContext.Provider value={value}>
      {children}
      <audio ref={remoteAudioRef} autoPlay className="voice-call-remote-audio" />
    </VoiceCallContext.Provider>
  );
}

export function useVoiceCall() {
  const context = useContext(VoiceCallContext);
  if (!context) throw new Error('useVoiceCall doit être utilisé dans VoiceCallProvider');
  return context;
}

export default VoiceCallContext;
